// Fixed CatalogPage.jsx with stabilized dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './ModernCatalog.css';
import { db, functions } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import CartModal from '../pages/CartModal';
import OrderConfirmation from '../pages/OrderConfirmation';
import NetworkStatus from '../components/NetworkStatus';
import ErrorHandler from '../components/ErrorHandler';
import useApi from '../utils/useApi';
import { validateEmail, validatePhone, validateRequired } from '../utils/ValidationUtils';
import Footer from '../components/Footer';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import HowItWorks from '../components/HowItWorks';
import { CategoryStories, PersonalTouch, OccasionStories, CraftStories } from '../components/StudioSections';
import PersonalizationBadge from '../components/PersonalizationBadge';
import './ValidationStyles.css';
import LazyImage from './LazyImage.jsx';
import OptimizedPagination from './OptimizedPagination';
import { useCart } from '../context/CartContext';
import { trackPurchase, trackSearch, trackCategoryFilter } from '../utils/analytics';
import { hapticSuccess } from '../utils/haptic';

const WHATSAPP_NUMBER = '+27650717182';
const ITEMS_PER_PAGE = 15;


// Define the API function outside the component to stabilize it
const fetchProductsFromFirestore = async () => {
  // Use 'products' collection to match what your dashboard uses
  const productsCollection = collection(db, 'products');
  
  // Simpler query that doesn't require a composite index
  const productsQuery = query(
    productsCollection,
    orderBy('name') // Just sort by name for now
  );
  
  const querySnapshot = await getDocs(productsQuery);
  const productsData = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }))
  // Filter active products client-side using isActive field
  .filter(product => product.isActive !== false);
  
  return productsData;
};

function CatalogPage() {
  const navigate = useNavigate();

  // Use CartContext for cart state
  const {
    cart,
    generalNote,
    setGeneralNote,
    isCartOpen,
    setIsCartOpen,
    groupedCartItems,
    totalItems,
    totalCost,
    addToCart,
    updateQuantity,
    updatePersonalization,
    updateCustomizationImages,
    removeFromCart,
    clearCart
  } = useCart();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Get URL parameters
  const location = useLocation();

  // New states for modern features
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortOption, setSortOption] = useState('name-asc');

  // Check URL parameters for tag filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tagParam = params.get('tag');

    if (params.get('section') === 'shop') {
      requestAnimationFrame(() => document.getElementById('shop')?.scrollIntoView({ block: 'start' }));
    }
    if (tagParam) {
      // Decode the tag (in case it has special characters or spaces)
      const decodedTag = decodeURIComponent(tagParam);
      setSelectedCategory(decodedTag);

      // Scroll to products section
      setTimeout(() => {
        const productsSection = document.querySelector('.filters-section');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.search]);

  // Form validation states
  const [formErrors, setFormErrors] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    cartItems: ''
  });

  // Customer information state
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  
  // Memoize the onSuccess callback to prevent it from changing on each render
  const handleProductsSuccess = useCallback((data) => {
    // Calculate pagination based on fetched products
    setFilteredProducts(data); // Initialize filtered products
    const total = Math.ceil(data.length / ITEMS_PER_PAGE);
    setTotalPages(total || 1); // Ensure at least one page
    updateDisplayedItems(1, data);
  }, []);
  
  // Use our custom API hook for products fetching
  const {
    data: allProducts = [],
    error: productsError,
    isLoading: loading,
    execute: fetchProducts,
    reset: resetProductsError
  } = useApi(fetchProductsFromFirestore, {
    onSuccess: handleProductsSuccess
  });
  
  // Function to update displayed items based on current page - memoize to stabilize
  const updateDisplayedItems = useCallback((page, products) => {
    if (!products || products.length === 0) {
      setDisplayedItems([]);
      return;
    }

    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedItems(products.slice(startIndex, endIndex));
  }, []);

  // Extract unique categories from products
  const categories = useCallback(() => {
    if (!allProducts || allProducts.length === 0) return ['All'];

    const cats = new Set(['All']);
    allProducts.forEach(product => {
      if (product.tags && Array.isArray(product.tags)) {
        product.tags.forEach(tag => cats.add(tag));
      }
    });
    return Array.from(cats);
  }, [allProducts]);

  // Filter products based on search and category
  const applyFilters = useCallback(() => {
    let filtered = [...allProducts];

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product =>
        product.tags && product.tags.includes(selectedCategory)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query)) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Apply sorting
    switch (sortOption) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        filtered.sort((a, b) => {
          const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
          const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        filtered.sort((a, b) => {
          const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
          const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
          return priceB - priceA;
        });
        break;
      default:
        // Default to name ascending
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredProducts(filtered);

    // Update pagination
    const total = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    setTotalPages(total || 1);
    setCurrentPage(1); // Reset to first page when filters change
    updateDisplayedItems(1, filtered);
  }, [allProducts, selectedCategory, searchQuery, sortOption, updateDisplayedItems]);

  // Apply filters when search, category, or sort option changes
  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      applyFilters();
    }
  }, [searchQuery, selectedCategory, sortOption, allProducts, applyFilters]);

  // Note: Cart and generalNote persistence is handled by CartContext

  // Handle product parameter from URL - redirect to product page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productParam = params.get('product');

    if (productParam && allProducts && allProducts.length > 0) {
      const product = allProducts.find(p => p.id === productParam);
      if (product) {
        // Navigate to the dedicated product page instead of opening modal
        navigate(`/product/${product.id}`, { replace: true });
      }
    }
  }, [location.search, allProducts, navigate]);

  // Memoize scroll handler for back-to-top button
  const handleScroll = useCallback(() => {
    // Show back to top button after scrolling down 400px
    setShowBackToTop(window.scrollY > 400);
  }, []);

  // Add scroll event listener for back-to-top button
  useEffect(() => {
    // Check initial scroll position on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);
  
  // Fetch products on component mount - with empty dependency array to only run once
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Update displayed items when page changes
  useEffect(() => {
    if (filteredProducts && filteredProducts.length > 0) {
      updateDisplayedItems(currentPage, filteredProducts);
    }
  }, [currentPage, filteredProducts, updateDisplayedItems]);
  
  // Pagination controls
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll is handled by OptimizedPagination component
    }
  }, [totalPages]);

  // Handle adding to cart (wrapper to clear form errors)
  const handleAddToCart = useCallback((item) => {
    // Use CartContext addToCart - it handles analytics and haptic feedback
    addToCart(item);

    // Clear any cart items error if it exists
    setFormErrors(prev => ({
      ...prev,
      cartItems: ''
    }));
  }, [addToCart]);

  // Function to format date consistently
  function formatDate(date) {
    const year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle returning to shop from confirmation
  const handleReturnToShop = useCallback(() => {
    setShowConfirmation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Generate WhatsApp message for order
  const generateWhatsAppMessage = useCallback((orderData) => {
    let message = `Hello, I'd like to order the following from Two Gs Crafting:%0A\n`;
    message += `Order Number: ${orderData.displayOrderId}%0A\n`;
    
    // Add customer information
    message += `Customer: ${orderData.customerInfo.name}%0A`;
    message += `Phone: ${orderData.customerInfo.phone}%0A`;
    if (orderData.customerInfo.email) {
      message += `Email: ${orderData.customerInfo.email}%0A`;
    }
    message += `%0A`;
    
    // Create a structured message by product
    Object.entries(groupedCartItems).forEach(([productId, product], index) => {
      message += `${index + 1}. ${product.name} - R${product.price} (${product.items.length} items total):%0A`;
      
      product.items.forEach((item, itemIndex) => {
        message += `   Item ${itemIndex + 1}: Quantity: ${item.quantity}, Total: R${product.price * item.quantity}`;
        if (item.personalization) {
          message += `%0A   Personalization: "${item.personalization}"`;
        }
        message += "%0A";
      });
      
      message += "%0A";
    });
    
    message += `Total Cost: R${orderData.totalAmount}`;
    
    if (orderData.generalNote) {
      message += `%0A\nGeneral Order Notes: ${orderData.generalNote}`;
    }
    
    message += `%0A\nOrder Reference: ${orderData.displayOrderId}`;
    
    return message;
  }, [groupedCartItems]);

  // Send order details to WhatsApp
  const sendToWhatsApp = useCallback(() => {
    if (!completedOrder) return;
    
    const message = generateWhatsAppMessage(completedOrder);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  }, [completedOrder, generateWhatsAppMessage]);

  // Validate customer information
  const validateCustomerInfo = useCallback(() => {
    let isValid = true;
    const newErrors = { ...formErrors };
    
    // Validate name (required)
    const nameValidation = validateRequired(customerInfo.name, 'Customer name');
    if (!nameValidation.valid) {
      newErrors.customerName = nameValidation.message;
      isValid = false;
    } else {
      newErrors.customerName = '';
    }
    
    // Validate phone (required)
    const phoneValidation = validatePhone(customerInfo.phone);
    if (!phoneValidation.valid) {
      newErrors.customerPhone = phoneValidation.message;
      isValid = false;
    } else {
      newErrors.customerPhone = '';
    }
    
    // Validate email (optional)
    if (customerInfo.email) {
      const emailValidation = validateEmail(customerInfo.email);
      if (!emailValidation.valid) {
        newErrors.customerEmail = emailValidation.message;
        isValid = false;
      } else {
        newErrors.customerEmail = '';
      }
    }
    
    setFormErrors(newErrors);
    return isValid;
  }, [customerInfo.name, customerInfo.phone, customerInfo.email, formErrors]);

  // Save order to Firestore and process with error handling
  const saveOrderAndProcess = async () => {
    // First validate the cart - make sure it has items
    if (cart.length === 0) {
      setFormErrors(prev => ({
        ...prev,
        cartItems: 'Your cart is empty. Please add items before checkout.'
      }));
      return;
    }
    if (cart.some(item => item.pricePending)) {
      setFormErrors(prev => ({ ...prev, cartItems: 'Pricing must be confirmed before checkout.' }));
      return;
    }
    
    // Validate customer information
    if (!validateCustomerInfo()) {
      return;
    }
    
    // Check for network connectivity
    if (!navigator.onLine) {
      setOrderError({
        message: 'You appear to be offline. Please check your internet connection and try again.',
        isOffline: true
      });
      return;
    }
    
    // Don't proceed if already submitting
    if (orderSubmitting) return;
    
    try {
      setOrderSubmitting(true);
      setOrderError(null);
      
      // Prepare the order data
      const orderItems = cart.map(item => ({
        productId: item.productId,
        productName: item.name,
        price: item.price,
        quantity: item.quantity,
        personalization: item.personalization || '',
        customizationImages: item.customizationImages || [],
        totalPrice: item.price * item.quantity
      }));
      
      const activeEvent = (() => {
        try { return JSON.parse(localStorage.getItem('twoGsActiveEventCollection') || 'null'); }
        catch { return null; }
      })();
      const requestedOrder = {
        items: orderItems,
        generalNote: generalNote || '',
        customerInfo: {
          name: customerInfo.name,
          phone: customerInfo.phone,
          email: customerInfo.email || '',
          contactMethod: 'Phone/Email'
        },
        eventCollectionSlug: activeEvent?.slug || null
      };

      const result = await httpsCallable(functions, 'createPublicOrder')(requestedOrder);
      const orderData = result.data;
      const displayOrderId = orderData.displayOrderId;

      // Store the completed order for the confirmation modal
      const completedOrderData = {
        ...orderData,
        id: orderData.id
      };

      // Track purchase in analytics
      trackPurchase(displayOrderId, orderItems, totalCost);

      // Haptic feedback for successful order
      hapticSuccess();

      // Close the cart and show order confirmation
      setIsCartOpen(false);
      setCompletedOrder(completedOrderData);
      setShowConfirmation(true);

      // Reset states - use clearCart from context
      clearCart();
      localStorage.removeItem('twoGsActiveEventCollection');
      setShowCustomerForm(false);
      setCustomerInfo({
        name: '',
        phone: '',
        email: ''
      });
      
    } catch (error) {
      console.error('Error saving order:', error);
      
      // Format user-friendly error message
      let errorMessage = 'There was an error processing your order. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'You don\'t have permission to create orders.';
      } else if (error.code === 'unavailable' || error.code === 'internal') {
        errorMessage = 'Our servers are currently unavailable. Please try again later.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cannot complete this action while offline.';
      }
      
      setOrderError({
        message: errorMessage,
        originalError: error,
        code: error.code || 'unknown'
      });
    } finally {
      setOrderSubmitting(false);
    }
  };

  // Handle order error dismissal
  const dismissOrderError = useCallback(() => {
    setOrderError(null);
  }, []);

  // Handle order retry
  const retryOrder = useCallback(() => {
    setOrderError(null);
    saveOrderAndProcess();
  }, []);

  // Generate pagination numbers
  const renderPaginationNumbers = () => {
    const pages = [];
    
    // Add first page
    pages.push(
      <button 
        key="first" 
        onClick={() => goToPage(1)} 
        className={`pagination-btn ${currentPage === 1 ? 'active' : ''}`}
      >
        1
      </button>
    );
    
    // Add ellipsis if needed
    if (currentPage > 3) {
      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
    }
    
    // Add pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i > 1 && i < totalPages) {
        pages.push(
          <button 
            key={i} 
            onClick={() => goToPage(i)} 
            className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          >
            {i}
          </button>
        );
      }
    }
    
    // Add ellipsis if needed
    if (currentPage < totalPages - 2) {
      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
    }
    
    // Add last page if there's more than one page
    if (totalPages > 1) {
      pages.push(
        <button 
          key="last" 
          onClick={() => goToPage(totalPages)} 
          className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
        >
          {totalPages}
        </button>
      );
    }
    
    return pages;
  };

  // Handle proceed to checkout
  const handleProceedToCheckout = useCallback(() => {
    // Validate cart has items
    if (cart.length === 0) {
      setFormErrors(prev => ({
        ...prev,
        cartItems: 'Your cart is empty. Please add items before checkout.'
      }));
      return;
    }

    setFormErrors(prev => ({
      ...prev,
      cartItems: ''
    }));

    setShowCustomerForm(true);
  }, [cart.length]);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  return (
    <>
      {/* Network Status Monitor */}
      <NetworkStatus />

      {/* New Two-Tier Header */}
      <Header
        cart={cart}
        onCartClick={() => setIsCartOpen(true)}
      />

      <main className="catalog-container" id="main-content">
        {/* Professional Hero Section */}
        <HeroSection />

        {/* How It Works Section */}
        <CategoryStories />

        {/* Filters Section */}
        <section className="filters-section" id="shop">
          <div className="shop-heading">
            <div>
              <span className="shop-kicker">Thoughtful. Useful. Uniquely theirs.</span>
              <h2>Made to be theirs.</h2>
            </div>
            <p>Explore gifts made to celebrate everyday moments, milestones and the people who matter most.</p>
          </div>
          <div className="category-chips">
            {categories().map(category => (
              <button
                key={category}
                className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category);
                  trackCategoryFilter(category);
                }}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="filter-summary">
            <div className="filter-summary-text">
              Showing {displayedItems.length} of {filteredProducts.length} products
              {searchQuery && ` for "${searchQuery}"`}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </div>
            <div className="sort-container">
              <label htmlFor="sort-select" className="sort-label">Sort by:</label>
              <select
                id="sort-select"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="sort-dropdown"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
              </select>
            </div>
          </div>
        </section>

        {/* Products Error Handler */}
        {productsError && (
          <ErrorHandler 
            error={productsError} 
            resetError={resetProductsError}
            retryAction={() => fetchProducts()}
          />
        )}

        {/* Order Error Handler */}
        {orderError && (
          <ErrorHandler 
            error={orderError} 
            resetError={dismissOrderError}
            retryAction={retryOrder}
          />
        )}

        {/* Products Section */}
        <section className="products-section">
          {loading ? (
            <div className="products-grid">
              {/* Show 12 skeleton cards while loading */}
              {[...Array(12)].map((_, index) => (
                <div key={index} className="product-card skeleton-card">
                  <div className="product-image-wrapper skeleton skeleton-block" style={{ height: '280px' }}></div>
                  <div className="product-info">
                    <div className="skeleton skeleton-text" style={{ height: '20px', width: '80%', marginBottom: '0.75rem' }}></div>
                    <div className="product-footer">
                      <div className="skeleton skeleton-text" style={{ height: '24px', width: '60px' }}></div>
                      <div className="skeleton skeleton-button" style={{ height: '32px', width: '70px', borderRadius: '20px' }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayedItems.length > 0 ? (
            <div className="products-grid">
              {displayedItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/product/${item.id}`}
                  className="product-card product-card-link"
                >
                  {/* Product Image */}
                  <div className="product-image-wrapper">
                    <LazyImage
                      src={
                        item.media && item.media.length > 0
                          ? item.media[0].url
                          : item.image
                      }
                      alt={item.name}
                      width="280"
                      height="280"
                      className="product-image"
                      fallbackSrc="/images/placeholder.jpg"
                    />
                    {item.discount > 0 && (
                      <div className="product-discount-badge">
                        {item.discount}% OFF
                      </div>
                    )}

                    {/* Personalization Badge - Bold & Modern style at bottom-left */}
                    <PersonalizationBadge variant="card" />
                  </div>

                  {/* Product Info - Compact Bold & Modern */}
                  <div className="product-info">
                    <h3 className="product-name">{item.name}</h3>

                    {/* Conditional Color & Size Options */}
                    {(item.colors?.length > 0 || item.sizes?.length > 0) && (
                      <div className="product-options">
                        {/* Color Swatches */}
                        {item.colors && item.colors.length > 0 && (
                          <div className="color-swatches">
                            {item.colors.map((color, idx) => (
                              <span
                                key={idx}
                                className="color-swatch"
                                style={{ backgroundColor: color.hex || color }}
                                title={color.name || color}
                              />
                            ))}
                          </div>
                        )}

                        {/* Size Options */}
                        {item.sizes && item.sizes.length > 0 && (
                          <div className="size-options">
                            {item.sizes.map((size, idx) => (
                              <span key={idx} className="size-chip">
                                {size}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="product-footer">
                      <div className="product-price-container">
                        {item.discount > 0 ? (
                          <>
                            <span className="product-price-original">
                              R{parseFloat(item.price).toFixed(2)}
                            </span>
                            <span className="product-price">
                              R{(parseFloat(item.price) * (1 - item.discount / 100)).toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="product-price">
                            R{parseFloat(item.price).toFixed(2)}
                          </span>
                        )}
                      </div>

                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h2 className="empty-state-title">
                {searchQuery || selectedCategory !== 'All'
                  ? 'No products found'
                  : 'No products available'}
              </h2>
              <p className="empty-state-text">
                {searchQuery || selectedCategory !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Please check back later for new items!'}
              </p>
            </div>
          )}
        </section>
        
        {/* Pagination Controls */}
        {/* {totalPages > 1 && !productsError && (
          <div className="pagination">
            <button 
              className="pagination-arrow" 
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &laquo; Prev
            </button>
            
            <div className="pagination-numbers">
              {renderPaginationNumbers()}
            </div>
            
            <button 
              className="pagination-arrow" 
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next &raquo;
            </button>
          </div>
        )} */}
        {totalPages > 1 && !productsError && (
          <OptimizedPagination
            totalItems={filteredProducts.length}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={goToPage}
          />
        )}

        <HowItWorks /><PersonalTouch /><OccasionStories /><CraftStories />
        {/* Product Detail Modal - Replaced with dedicated ProductPage */}

        {/* Cart Modal with Customer Info Form - Pass validation errors */}
        <CartModal
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          cart={cart}
          groupedCartItems={groupedCartItems}
          updateQuantity={updateQuantity}
          removeFromCart={removeFromCart}
          updatePersonalization={updatePersonalization}
          updateCustomizationImages={updateCustomizationImages}
          addToCart={handleAddToCart}
          catalog={allProducts}
          totalCost={totalCost}
          generalNote={generalNote}
          setGeneralNote={setGeneralNote}
          handleSubmitOrder={saveOrderAndProcess}
          orderSubmitting={orderSubmitting}
          // Customer info props
          showCustomerForm={showCustomerForm}
          setShowCustomerForm={setShowCustomerForm}
          customerInfo={customerInfo}
          setCustomerInfo={setCustomerInfo}
          // Validation props
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          handleProceedToCheckout={handleProceedToCheckout}
          // Error handling
          orderError={orderError}
          dismissOrderError={dismissOrderError}
          retryOrder={retryOrder}
          // Product navigation handler - navigate to product page instead of modal
          onOpenProductModal={(product) => navigate(`/product/${product.id}`)}
        />
        
        {/* Order Confirmation Modal */}
        <OrderConfirmation
          isOpen={showConfirmation}
          onClose={handleReturnToShop}
          orderDetails={completedOrder}
          handleReturnToShop={handleReturnToShop}
          sendToWhatsApp={sendToWhatsApp}
          emailSent={customerInfo.email && customerInfo.email.trim() !== ''}
        />

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            ↑
          </button>
        )}
      </main>

      {/* Professional Footer */}
      <Footer />
    </>
  );
}

export default CatalogPage;
